import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Empty, ErrorState, Loading } from '@/components/ui/state';
import { formatAge } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Employee {
  employeeId: number;
  name: string;
  email: string;
  employeeCode: string;
  designation: string | null;
  trackingEnabled: boolean;
  trackingMode: string;
  status: string;
  deviceCount: number;
  lastSeenAt: string | null;
  locationPermission: string | null;
}

export function EmployeesPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [query, setQuery] = useState('');

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['employees'],
    queryFn: () => api.get<Employee[]>('/admin/employees'),
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/admin/employees', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['employees'] });
      setShowForm(false);
    },
  });

  const employees = data?.data ?? [];

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return employees;
    return employees.filter((e) =>
      `${e.name} ${e.email} ${e.employeeCode} ${e.designation ?? ''}`
        .toLowerCase()
        .includes(needle),
    );
  }, [employees, query]);

  const ageSeconds = (iso: string | null): number | null =>
    iso === null ? null : Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));

  if (isPending) return <Loading label="Loading employees…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  // The count that matters on this page: an employee without background
  // permission is enrolled and cannot report once the app leaves the
  // foreground, which is the single most common reason a device looks set up
  // and never appears on the map.
  const withoutBackground = employees.filter((e) => e.locationPermission !== 'always').length;

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Employees</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {employees.length} enrolled
            {withoutBackground > 0 ? (
              <>
                {' · '}
                <span className="text-warning">
                  {withoutBackground} without background location
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            aria-label="Search employees"
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
          />
          <button
            onClick={() => setShowForm((v) => !v)}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            {showForm ? 'Cancel' : 'Add employee'}
          </button>
        </div>
      </header>

      {showForm && (
        <CreateForm
          onSubmit={(body) => create.mutate(body)}
          busy={create.isPending}
          error={create.error instanceof ApiError ? create.error : null}
        />
      )}

      {employees.length === 0 ? (
        <Empty title="No employees yet" hint="Add one, then sign in with it on the mobile app." />
      ) : visible.length === 0 ? (
        <Empty title="Nobody matches that search" />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Code</th>
                <th className="px-4 py-2.5 font-medium">Name</th>
                <th className="px-4 py-2.5 font-medium">Mode</th>
                <th className="px-4 py-2.5 font-medium">Devices</th>
                <th className="px-4 py-2.5 font-medium">Background permission</th>
                <th className="px-4 py-2.5 font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map((e) => (
                <tr key={e.employeeId} className="transition-colors hover:bg-muted/40">
                  <td className="px-4 py-2.5 font-mono text-xs">{e.employeeCode}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {e.designation ? `${e.designation} · ` : ''}
                      {e.email}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={cn(!e.trackingEnabled && 'text-muted-foreground line-through')}
                    >
                      {e.trackingMode.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 tabular-nums">{e.deviceCount}</td>
                  <td className="px-4 py-2.5">
                    <PermissionBadge value={e.locationPermission} />
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                    {e.lastSeenAt ? formatAge(ageSeconds(e.lastSeenAt)) : 'never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </section>
  );
}

function PermissionBadge({ value }: { value: string | null }) {
  if (value === 'always') return <Badge tone="success">Always</Badge>;
  if (value === 'while_in_use') return <Badge tone="warning">Foreground only</Badge>;
  if (value === 'denied') return <Badge tone="danger">Denied</Badge>;
  return <Badge tone="muted">No device</Badge>;
}

function CreateForm({
  onSubmit,
  busy,
  error,
}: {
  onSubmit: (body: Record<string, unknown>) => void;
  busy: boolean;
  error: ApiError | null;
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    employeeCode: '',
    designation: '',
    trackingMode: 'shift_only',
  });

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // When the server named the offending fields, they are shown against those
  // fields and the generic banner would only add noise.
  const hasFieldErrors = Object.keys(error?.fieldErrors ?? {}).length > 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: form.name,
          email: form.email,
          password: form.password,
          employeeCode: form.employeeCode,
          ...(form.designation ? { designation: form.designation } : {}),
          trackingMode: form.trackingMode,
        });
      }}
      className="grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-2"
    >
      {(
        [
          ['name', 'Full name', 'text', 2],
          ['email', 'Work email', 'email', 0],
          ['password', 'Temporary password', 'text', 10],
          ['employeeCode', 'Employee code', 'text', 1],
          ['designation', 'Designation (optional)', 'text', 0],
        ] as const
      ).map(([key, label, type, minLength]) => {
        // The API already returns which field failed and why. Showing only the
        // envelope's summary — "The given data was invalid." — threw that away
        // and left the person guessing which of five inputs was wrong.
        const fieldError = error?.fieldErrors?.[key]?.[0];
        return (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {label}
              {minLength > 1 && (
                <span className="ml-1 font-normal">(min {minLength} characters)</span>
              )}
            </label>
            <input
              type={type}
              required={key !== 'designation'}
              // Enforced by the browser too, so the common mistakes never cost
              // a round trip. The server still validates — this is convenience,
              // not the control.
              {...(minLength > 0 ? { minLength } : {})}
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
              aria-invalid={Boolean(fieldError)}
              className={cn(
                'w-full rounded-md border bg-background px-3 py-1.5 text-sm',
                fieldError ? 'border-danger' : 'border-border',
              )}
            />
            {fieldError && <p className="text-xs text-danger">{fieldError}</p>}
          </div>
        );
      })}

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Tracking mode</label>
        <select
          value={form.trackingMode}
          onChange={(e) => set('trackingMode', e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        >
          <option value="always">Always — you start and stop tracking from the live map</option>
          <option value="shift_only">Shift only — tracking is tied to a scheduled shift</option>
          <option value="manual">Manual</option>
          <option value="disabled">Disabled — this employee is never tracked</option>
        </select>
      </div>

      {error && !hasFieldErrors && (
        <p className="text-sm text-danger sm:col-span-2">
          {error.message} <span className="font-mono text-xs">({error.code})</span>
        </p>
      )}

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {busy ? 'Creating…' : 'Create employee'}
        </button>
      </div>
    </form>
  );
}
